// js/_shared/_theme.js
/* =============================================================================
   SHARED THEME MANAGER
   Path: /js/_shared/_theme.js
   
   Purpose: Apply and manage theme colors via CSS custom properties.
            Handles hex-to-RGB conversion and automatic contrast color calculation.
   
   Dependencies: 
   - /js/_shared/_utils.js (load first)
   
   Namespace: window.AppUtils.Theme
   Global Alias: window.Theme (for convenience)
   ========================================================================== */

window.AppUtils = window.AppUtils || {};

window.AppUtils.Theme = (function () {
    'use strict';

    /* =========================================================================
       COLOR UTILITIES
       ========================================================================= */

    /**
     * Convert hex color to RGB string
     * @param {string} hex - Hex color (e.g., '#667eea' or '667eea')
     * @returns {string} RGB as "102, 126, 234"
     */
    function hexToRgb(hex) {
        if (!hex) return '102, 126, 234'; // Default purple

        // Remove # if present
        hex = hex.replace(/^#/, '');

        // Handle shorthand (e.g., 'fff' -> 'ffffff')
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        var r = parseInt(hex.substring(0, 2), 16);
        var g = parseInt(hex.substring(2, 4), 16);
        var b = parseInt(hex.substring(4, 6), 16);

        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            return '102, 126, 234'; // Default purple
        }

        return r + ', ' + g + ', ' + b;
    }

    /**
     * Calculate contrast color for text readability
     * Uses luminance formula to determine if white or dark text is needed
     * @param {string} hex - Background hex color
     * @returns {string} '#ffffff' for dark backgrounds, '#1f2937' for light
     */
    function getContrastColor(hex) {
        if (!hex) return '#ffffff';

        hex = hex.replace(/^#/, '');

        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        var r = parseInt(hex.substring(0, 2), 16);
        var g = parseInt(hex.substring(2, 4), 16);
        var b = parseInt(hex.substring(4, 6), 16);

        if (isNaN(r) || isNaN(g) || isNaN(b)) {
            return '#ffffff';
        }

        // Calculate luminance
        var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

        // Return white for dark backgrounds, dark gray for light backgrounds
        // Threshold set at 0.6 to favor white text on mid-tone colors (e.g., coral, 
        // orange, medium blues) where white is more readable than dark text.
        return luminance > 0.6 ? '#1f2937' : '#ffffff';
    }

    /* =========================================================================
       DARK MODE UTILITIES
       ========================================================================= */

    /**
     * Check if the page is currently in dark mode
     * @returns {boolean}
     */
    function isDarkMode() {
        return document.documentElement.getAttribute('data-bs-theme') === 'dark';
    }

    /**
     * Parse hex color to {r,g,b}
     * @param {string} hex
     * @returns {{r:number, g:number, b:number}|null}
     */
    function parseHex(hex) {
        if (!hex) return null;
        hex = hex.replace(/^#/, '');
        if (hex.length === 3) {
            hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }
        var r = parseInt(hex.substring(0, 2), 16);
        var g = parseInt(hex.substring(2, 4), 16);
        var b = parseInt(hex.substring(4, 6), 16);
        if (isNaN(r) || isNaN(g) || isNaN(b)) return null;
        return { r: r, g: g, b: b };
    }

    /**
     * Convert {r,g,b} back to hex string
     * @param {{r:number, g:number, b:number}} c
     * @returns {string}
     */
    function rgbToHex(c) {
        return '#' +
            ('0' + Math.round(c.r).toString(16)).slice(-2) +
            ('0' + Math.round(c.g).toString(16)).slice(-2) +
            ('0' + Math.round(c.b).toString(16)).slice(-2);
    }

    /**
     * WCAG 2.0 relative luminance (same algorithm as ThemeCssVars.cs)
     * @param {{r:number, g:number, b:number}} c
     * @returns {number}
     */
    function getRelativeLuminance(c) {
        var rs = c.r / 255, gs = c.g / 255, bs = c.b / 255;
        var rl = rs <= 0.03928 ? rs / 12.92 : Math.pow((rs + 0.055) / 1.055, 2.4);
        var gl = gs <= 0.03928 ? gs / 12.92 : Math.pow((gs + 0.055) / 1.055, 2.4);
        var bl = bs <= 0.03928 ? bs / 12.92 : Math.pow((bs + 0.055) / 1.055, 2.4);
        return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
    }

    /**
     * WCAG contrast ratio between two colors
     * @param {{r:number, g:number, b:number}} c1
     * @param {{r:number, g:number, b:number}} c2
     * @returns {number}
     */
    function getContrastRatio(c1, c2) {
        var l1 = getRelativeLuminance(c1);
        var l2 = getRelativeLuminance(c2);
        var lighter = Math.max(l1, l2);
        var darker = Math.min(l1, l2);
        return (lighter + 0.05) / (darker + 0.05);
    }

    /**
     * Lighten a color to meet minimum contrast against a dark background.
     * Mirrors ThemeCssVars.EnsureMinimumContrast() from server.
     * @param {string} hex - Color to adjust
     * @param {string} bgHex - Background color (e.g., '#212529')
     * @param {number} minRatio - Minimum contrast ratio (default 4.5 for WCAG AA)
     * @returns {string} Adjusted hex color
     */
    function ensureMinimumContrast(hex, bgHex, minRatio) {
        var fg = parseHex(hex);
        var bg = parseHex(bgHex);
        if (!fg || !bg) return hex;
        minRatio = minRatio || 4.5;

        if (getContrastRatio(fg, bg) >= minRatio) return hex;

        // Lighten in small steps until we meet the ratio
        for (var i = 0; i < 100; i++) {
            fg.r = Math.min(255, fg.r + 3);
            fg.g = Math.min(255, fg.g + 3);
            fg.b = Math.min(255, fg.b + 3);
            if (getContrastRatio(fg, bg) >= minRatio) break;
        }
        return rgbToHex(fg);
    }

    /**
     * Darken a color to meet minimum contrast against white (#ffffff).
     * Used to generate accessible text/border variants of light brand colors.
     * @param {string} hex - Color to adjust
     * @param {number} minRatio - Minimum contrast ratio (default 4.5 for WCAG AA)
     * @returns {string} Adjusted hex color (unchanged if already accessible)
     */
    function darkenForLightBg(hex, minRatio) {
        var fg = parseHex(hex);
        if (!fg) return hex;
        minRatio = minRatio || 4.5;
        var white = { r: 255, g: 255, b: 255 };

        if (getContrastRatio(fg, white) >= minRatio) return hex;

        // Darken in small steps until we meet the ratio
        for (var i = 0; i < 100; i++) {
            fg.r = Math.max(0, fg.r - 3);
            fg.g = Math.max(0, fg.g - 3);
            fg.b = Math.max(0, fg.b - 3);
            if (getContrastRatio(fg, white) >= minRatio) break;
        }
        return rgbToHex(fg);
    }

    /**
     * Lighten a color to meet minimum contrast against a dark background (#2b3035).
     * Companion to darkenForLightBg() — handles dark brand colors on dark themes.
     * @param {string} hex - Color to adjust
     * @param {number} minRatio - Minimum contrast ratio (default 4.5 for WCAG AA)
     * @returns {string} Adjusted hex color (unchanged if already accessible)
     */
    function lightenForDarkBg(hex, minRatio) {
        var fg = parseHex(hex);
        if (!fg) return hex;
        minRatio = minRatio || 4.5;
        var darkBg = { r: 43, g: 48, b: 53 }; // #2b3035 - dark mode card background

        if (getContrastRatio(fg, darkBg) >= minRatio) return hex;

        // Lighten in small steps until we meet the ratio
        for (var i = 0; i < 100; i++) {
            fg.r = Math.min(255, fg.r + 3);
            fg.g = Math.min(255, fg.g + 3);
            fg.b = Math.min(255, fg.b + 3);
            if (getContrastRatio(fg, darkBg) >= minRatio) break;
        }
        return rgbToHex(fg);
    }

    /* =========================================================================
       THEME APPLICATION
       ========================================================================= */

    // Track last applied theme so we can re-apply on dark mode toggle
    var _lastTheme = null;
    var _lastTarget = null;
    var _observerAttached = false;

    /**
     * Apply theme colors to an element via CSS custom properties.
     * In dark mode, adjusts primary/secondary for contrast on dark backgrounds
     * and skips --neutral (CSS dark rules handle it).
     * Automatically re-applies when data-bs-theme changes (dark mode toggle).
     * @param {Object} theme - Theme object { primary, secondary, neutral }
     * @param {Element} [target=document.documentElement] - Target element
     */
    function apply(theme, target) {
        if (!theme) return;

        var el = target || document.documentElement;
        var dark = isDarkMode();

        // Primary color — use as-is (brand colors are not auto-adjusted)
        if (theme.primary) {
            el.style.setProperty('--primary', theme.primary);
            el.style.setProperty('--primary-rgb', hexToRgb(theme.primary));
            el.style.setProperty('--primary-text', getContrastColor(theme.primary));
            // Accessible variant: mode-aware for text/borders
            // Light mode: darken light colors for contrast on white
            // Dark mode: lighten dark colors for contrast on #2b3035
            el.style.setProperty('--primary-accessible',
                dark ? lightenForDarkBg(theme.primary) : darkenForLightBg(theme.primary));
        }

        // Secondary color — use as-is (brand colors are not auto-adjusted)
        if (theme.secondary) {
            el.style.setProperty('--secondary', theme.secondary);
            el.style.setProperty('--secondary-rgb', hexToRgb(theme.secondary));
            el.style.setProperty('--secondary-text', getContrastColor(theme.secondary));
            // Accessible variant: mode-aware for text/borders
            el.style.setProperty('--secondary-accessible',
                dark ? lightenForDarkBg(theme.secondary) : darkenForLightBg(theme.secondary));
        }

        // Neutral color — in dark mode, use dark background token instead
        if (theme.neutral) {
            if (dark) {
                // Don't set --neutral inline; let CSS dark rules handle it
                // Remove any previously-set inline --neutral so CSS can take over
                el.style.removeProperty('--neutral');
                el.style.removeProperty('--neutral-rgb');
            } else {
                el.style.setProperty('--neutral', theme.neutral);
                el.style.setProperty('--neutral-rgb', hexToRgb(theme.neutral));

                // Toggle dark-neutral class for contrast-aware styling
                // (elements on neutral bg get light text; cards keep dark text)
                var bg = parseHex(theme.neutral);
                if (bg) {
                    var lum = getRelativeLuminance(bg);
                    if (lum < 0.4) {
                        el.classList.add('dark-neutral');
                    } else {
                        el.classList.remove('dark-neutral');
                    }
                }
            }
        }

        // Remember last theme/target and watch for dark mode changes
        _lastTheme = theme;
        _lastTarget = target;
        if (!_observerAttached) {
            _observerAttached = true;
            new MutationObserver(function () {
                if (_lastTheme) {
                    apply(_lastTheme, _lastTarget);
                }
            }).observe(document.documentElement, {
                attributes: true,
                attributeFilter: ['data-bs-theme']
            });
        }
    }

    /**
     * Apply theme from element's data attributes
     * Looks for data-primary-color, data-secondary-color, data-neutral-color
     * Also supports data-theme-primary, data-theme-secondary, data-theme-neutral
     * @param {Element} element - Element with data attributes
     */
    function applyFromDataset(element) {
        if (!element || !element.dataset) return;

        var theme = {
            primary: element.dataset.primaryColor || element.dataset.themePrimary,
            secondary: element.dataset.secondaryColor || element.dataset.themeSecondary,
            neutral: element.dataset.neutralColor || element.dataset.themeNeutral
        };

        apply(theme, element);
    }

    /**
     * Get a CSS custom property value
     * @param {string} property - Property name (with or without --)
     * @param {Element} [element=document.documentElement] - Element to read from
     * @returns {string} Property value
     */
    function getProperty(property, element) {
        var el = element || document.documentElement;
        var prop = property.startsWith('--') ? property : '--' + property;
        return getComputedStyle(el).getPropertyValue(prop).trim();
    }

    /* =========================================================================
       PUBLIC API
       ========================================================================= */

    return {
        // Color utilities
        hexToRgb: hexToRgb,
        getContrastColor: getContrastColor,
        ensureMinimumContrast: ensureMinimumContrast,
        darkenForLightBg: darkenForLightBg,
        lightenForDarkBg: lightenForDarkBg,

        // Dark mode
        isDarkMode: isDarkMode,

        // Theme application
        apply: apply,
        applyFromDataset: applyFromDataset,

        // Property access
        getProperty: getProperty
    };

})();

/* =============================================================================
   GLOBAL ALIAS
   ========================================================================== */

window.Theme = window.AppUtils.Theme;
